/* eslint-disable import/no-cycle */
import {
  fetchOfferVersion,
  fetchOverview,
} from '@/composables/billing/billingApi';
import { BillingOverview, ShopOffer } from '@/types/billing-types';

export interface BillingState {
  shopOffer: ShopOffer | null;
  overview: BillingOverview | null;
  loadingOffer: boolean;
  loadingOverview: boolean;
  loadedOverview: boolean;
  lastFetchedAt: number | null;
}

/**
 * Single source of truth for the new billing UI on the frontend.
 *
 * - `shopOffer` is refetched at app mount AND on every shop switch (cf. D1).
 *   It feeds the router redirect that decides whether to show BillingV3Frame
 *   (V3 shops) or redirect to /shop-subscription (V4/V5).
 * - `overview` is refetched when SubscriptionPage mounts and after every non-
 *   structural action (e.g. buying SMS credits). Structural actions trigger
 *   a full window.location.reload() instead (cf. D3).
 */
export default {
  namespaced: true,
  state: (): BillingState => ({
    shopOffer: null,
    overview: null,
    loadingOffer: false,
    loadingOverview: false,
    loadedOverview: false,
    lastFetchedAt: null,
  }),

  getters: {
    offerVersion: (state: BillingState) => state.shopOffer?.offerVersion ?? null,
    canMigrateToV5: (state: BillingState) => state.shopOffer?.canMigrateToV5 ?? false,
    isV3: (state: BillingState) => state.shopOffer?.offerVersion === 'v3',
    isReady: (state: BillingState) => state.shopOffer !== null,
  },

  mutations: {
    setShopOffer(state: BillingState, payload: ShopOffer | null) {
      state.shopOffer = payload;
    },
    setOverview(state: BillingState, payload: BillingOverview | null) {
      state.overview = payload;
      state.loadedOverview = payload !== null;
      state.lastFetchedAt = payload !== null ? Date.now() : state.lastFetchedAt;
    },
    setLoadingOffer(state: BillingState, value: boolean) {
      state.loadingOffer = value;
    },
    setLoadingOverview(state: BillingState, value: boolean) {
      state.loadingOverview = value;
    },
    reset(state: BillingState) {
      state.shopOffer = null;
      state.overview = null;
      state.loadingOffer = false;
      state.loadingOverview = false;
      state.loadedOverview = false;
      state.lastFetchedAt = null;
    },
  },

  actions: {
    async fetchShopOffer({ commit }: any): Promise<ShopOffer | null> {
      commit('setLoadingOffer', true);
      try {
        const offer = await fetchOfferVersion();
        commit('setShopOffer', offer);
        return offer;
      } finally {
        commit('setLoadingOffer', false);
      }
    },

    async refetchOverview({ commit }: any): Promise<BillingOverview | null> {
      commit('setLoadingOverview', true);
      try {
        const overview = await fetchOverview();
        commit('setOverview', overview);
        return overview;
      } finally {
        commit('setLoadingOverview', false);
      }
    },
  },
};
